import { Signal } from '@angular/core';
import { Observable } from 'rxjs';
import { AccesoUsuario, Credenciales, SesionUsuario } from './auth.types';

/**
 * CONTRATO de sesion contra ApiUserManager (`/api/v1/sesion/*`).
 *
 * Es una clase abstracta y no una interfaz a proposito: en Angular una interfaz
 * desaparece al compilar y no sirve como token de inyeccion, mientras que una
 * abstracta se inyecta directa y el compilador verifica que la implementacion la
 * cumpla entera.
 *
 * Lo cumplen `AuthSimuladoService` y `AuthHttpService`; elige `app.config.ts`
 * con UN provider, segun `environment.authSimulado`.
 */
export abstract class AuthService {
  /** Sesion activa, o `null` si no hay. */
  abstract readonly sesion: Signal<SesionUsuario | null>;
  /** Atajo legible para guardas y plantillas. */
  abstract readonly autenticado: Signal<boolean>;

  /** Las tarjetas, en el orden que mando el backend. Vacia sin sesion. */
  abstract readonly accesos: Signal<readonly AccesoUsuario[]>;

  /**
   * El acceso elegido, o `null` si todavia no se eligio.
   *
   * `null` con sesion NO significa "algo salio mal": significa "pinta la
   * rejilla". Es el estado que decide la pantalla.
   */
  abstract readonly accesoActivo: Signal<AccesoUsuario | null>;

  abstract iniciarSesion(credenciales: Credenciales): Observable<void>;

  /**
   * Reemite la sesion con esa tarjeta activa y **devuelve el acceso que el
   * servidor RESOLVIO**, que puede no ser el que se pidio.
   *
   * ⚠️ Hay que llamarlo ANTES de navegar. Sin esto el token va sin acceso
   * activo, asi que la aplicacion de destino recibe `accesoActivo: null` y
   * vuelve a pintar la rejilla — el usuario rebota entre las dos pantallas sin
   * ningun error.
   *
   * Y el destino sale de lo que devuelve, no de la tarjeta que se pulso:
   * `ResolverAcceso` del servidor intenta la coincidencia exacta y luego cae al
   * candidato unico, y para el superadministrador —un rol, varias aplicaciones—
   * eso puede no coincidir con lo que el cliente creia estar pidiendo.
   */
  abstract elegirAcceso(acceso: AccesoUsuario): Observable<AccesoUsuario>;

  /**
   * Cambia la contraseña. Devuelve la sesion renovada, **con la bandera
   * `debeCambiarContrasena` ya apagada y con los accesos**, asi que se puede
   * pintar la rejilla con esa respuesta sin una segunda llamada.
   *
   * ⚠️ El endpoint **invalida las demas sesiones del usuario**, a proposito:
   * quien cambia su contraseña suele hacerlo porque sospecha que otro entro. Al
   * probarlo con dos pestañas abiertas, la otra se cae — no esta roto.
   */
  abstract cambiarContrasena(actual: string, nueva: string): Observable<void>;

  /**
   * Cierra la sesion. Devuelve un observable y no `void` porque con cookies
   * `HttpOnly` el cierre REAL solo puede hacerlo el servidor: JavaScript no
   * puede borrar esas cookies.
   *
   * ⚠️ **PUEDE FALLAR, Y EL FALLO HAY QUE MOSTRARLO.** Aqui decia "no falla
   * nunca: el estado local se limpia igual aunque la peticion se caiga". La
   * premisa estaba invertida, y es justo lo que vuelve grave el defecto:
   *
   *   se limpio el estado local     → la interfaz muestra el login
   *   las cookies siguen puestas    → nadie las borro
   *   el refresco sigue vigente     → `sesion.refresco` no se toco
   *   una recarga llama a `actual`, responde 200, y el usuario esta DENTRO
   *
   * O sea: ve la pantalla de login, cree que salio, y no salio. En una maquina
   * compartida eso es que el siguiente recargue y este en su sesion.
   *
   * La interfaz NO PUEDE terminar una sesion. Fingir que si es lo unico que no
   * debe hacer, asi que el estado local se limpia de forma optimista —para no
   * dejar datos a la vista— pero el error se PROPAGA para que la pantalla lo
   * diga y ofrezca reintentar.
   */
  abstract cerrarSesion(): Observable<void>;

  /**
   * Rehidrata la sesion al arrancar (recarga de pagina, pestaña nueva).
   *
   * Es asincrona porque no hay token legible que inspeccionar: la unica forma de
   * saber si hay sesion es preguntarsela al servidor. `app.config.ts` la
   * DEVUELVE en su inicializador para que Angular espere antes del primer
   * render; sin eso, un F5 pinta el login por un instante y el parpadeo se lee
   * como un cierre de sesion espurio.
   */
  abstract restaurarSesion(): Observable<void>;
}
